import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Send, Paperclip, Download, FileText, Image as ImageIcon, ArrowLeft,
  CheckCircle2, XCircle, PlayCircle, Upload, RotateCw, Archive, Clock, MapPin, IndianRupee, Flag,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api, { API_ORIGIN } from "../../lib/api";
import { useAuth } from "../../context/AppContext";

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
};
const fmtDate = (iso) => new Date(iso).toDateString();
const fmtBytes = (n) => {
  if (!n) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const PRIORITY_COLOR = {
  low: "#64748B", normal: "#3B82F6", high: "#F59E0B", urgent: "#F43F5E",
};

const STATUS_BADGE = {
  sent:         { label: "Sent",         bg: "rgba(59,130,246,0.10)", border: "rgba(59,130,246,0.35)", color: "#3B82F6" },
  accepted:     { label: "Accepted",     bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.35)", color: "#10B981" },
  rejected:     { label: "Rejected",     bg: "rgba(244,63,94,0.10)",  border: "rgba(244,63,94,0.35)",  color: "#F43F5E" },
  in_progress:  { label: "In Progress",  bg: "rgba(6,182,212,0.10)",  border: "rgba(6,182,212,0.35)",  color: "#06B6D4" },
  submitted:    { label: "Submitted",    bg: "rgba(234,179,8,0.10)",  border: "rgba(234,179,8,0.35)",  color: "#EAB308" },
  revision:     { label: "Revision",     bg: "rgba(249,115,22,0.10)", border: "rgba(249,115,22,0.35)", color: "#F97316" },
  approved:     { label: "Approved",     bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.35)", color: "#10B981" },
  completed:    { label: "Completed",    bg: "rgba(16,185,129,0.10)", border: "rgba(16,185,129,0.35)", color: "#10B981" },
  archived:     { label: "Archived",     bg: "rgba(100,116,139,0.10)",border: "rgba(100,116,139,0.35)",color: "#64748B" },
};

const StatusPill = ({ status }) => {
  const s = STATUS_BADGE[status] || STATUS_BADGE.sent;
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border"
          data-testid={`dw-status-${status}`}
          style={{ background: s.bg, borderColor: s.border, color: s.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.color }}/>
      {s.label}
    </span>
  );
};

const TaskCard = ({ dw }) => (
  <div className="rounded-lg border p-4" data-testid="dw-task-card"
       style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
    <div className="flex items-start justify-between gap-3 mb-3">
      <div>
        <div className="eyebrow" style={{ color: "var(--text-dim)" }}>Task</div>
        <h3 className="font-display text-lg mt-0.5" style={{ color: "var(--text)" }}>{dw.title}</h3>
      </div>
      <StatusPill status={dw.status}/>
    </div>
    <p className="text-sm mb-3 whitespace-pre-line" style={{ color: "var(--text-muted)" }}>{dw.description}</p>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
      <Meta icon={Clock}       label="Deadline" value={dw.deadline || "—"}/>
      <Meta icon={MapPin}      label="Location" value={dw.location || dw.city || "Remote"}/>
      <Meta icon={IndianRupee} label="Budget"   value={dw.budget ? `₹${Number(dw.budget).toLocaleString("en-IN")}` : "—"}/>
      <Meta icon={Flag}        label="Priority" value={(dw.priority || "normal").toUpperCase()} color={PRIORITY_COLOR[dw.priority || "normal"]}/>
    </div>
    {(dw.instructions || dw.required_skills?.length > 0 || dw.attachments?.length > 0) && (
      <div className="mt-3 pt-3 border-t space-y-2" style={{ borderColor: "var(--border)" }}>
        {dw.instructions && (
          <div className="text-xs whitespace-pre-line" style={{ color: "var(--text-muted)" }}>
            <span className="font-medium" style={{ color: "var(--text)" }}>Instructions: </span>{dw.instructions}
          </div>
        )}
        {dw.required_skills?.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {dw.required_skills.map(s => (
              <span key={s} className="text-[10px] px-2 py-0.5 rounded-full border"
                    style={{ borderColor: "var(--border-strong)", color: "var(--text-muted)" }}>{s}</span>
            ))}
          </div>
        )}
        {dw.attachments?.length > 0 && (
          <div className="space-y-1">
            {dw.attachments.map(a => <AttachmentRow key={a.file_id} file={a}/>)}
          </div>
        )}
      </div>
    )}
  </div>
);

const Meta = ({ icon: Icon, label, value, color }) => (
  <div className="flex items-center gap-2 px-2 py-1.5 rounded-md" style={{ background: "var(--surface)" }}>
    <Icon size={12} style={{ color: color || "var(--text-dim)" }}/>
    <div className="min-w-0">
      <div className="text-[9px] uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>{label}</div>
      <div className="text-xs truncate" style={{ color: color || "var(--text)" }}>{value}</div>
    </div>
  </div>
);

const AttachmentRow = ({ file }) => {
  const token = (typeof window !== "undefined") ? localStorage.getItem("session_token") : null;
  const url = `${API_ORIGIN}/api/files/${file.file_id}/download${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
  const isImage = (file.content_type || "").startsWith("image/");
  return (
    <a
      href={url} target="_blank" rel="noreferrer"
      data-testid={`dw-attachment-${file.file_id}`}
      className="flex items-center gap-2 p-2 rounded-md border text-xs hover:bg-white/5 transition-colors"
      style={{ borderColor: "var(--border-strong)", background: "var(--surface)" }}
    >
      {isImage ? <ImageIcon size={14} style={{ color: "var(--brand)" }}/> : <FileText size={14} style={{ color: "var(--brand)" }}/>}
      <span className="flex-1 truncate" style={{ color: "var(--text)" }}>{file.original_filename || file.file_id}</span>
      <span className="text-[10px]" style={{ color: "var(--text-dim)" }}>{fmtBytes(file.size)}</span>
      <Download size={12} style={{ color: "var(--text-dim)" }}/>
    </a>
  );
};

const Bubble = ({ m, mine }) => {
  if (m.kind === "system") {
    return (
      <div className="my-2 flex justify-center">
        <span className="text-[10px] px-3 py-1 rounded-full border" style={{
          background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text-muted)",
        }}>{m.text}</span>
      </div>
    );
  }
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} my-1`} data-testid={`dw-msg-${m.message_id}`}>
      <div className="max-w-[75%] rounded-2xl px-3.5 py-2 text-sm"
           style={{
             background: mine ? "linear-gradient(180deg,#3B82F6,#2563EB)" : "var(--surface-2)",
             color: mine ? "#fff" : "var(--text)",
             borderTopRightRadius: mine ? 6 : 16,
             borderTopLeftRadius: mine ? 16 : 6,
             border: mine ? "none" : "1px solid var(--border)",
           }}>
        {!mine && m.sender_name && (
          <div className="text-[10px] mb-0.5 opacity-70">{m.sender_name}</div>
        )}
        {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
        {m.attachments?.length > 0 && (
          <div className="mt-2 space-y-1">
            {m.attachments.map(a => <AttachmentRow key={a.file_id} file={a}/>)}
          </div>
        )}
        <div className={`mt-1 text-[10px] ${mine ? "opacity-80" : ""}`} style={{ color: mine ? "rgba(255,255,255,0.85)" : "var(--text-dim)" }}>
          {fmtTime(m.created_at)}{mine && m.read_by?.length > 1 && " · Read"}
        </div>
      </div>
    </div>
  );
};

const Timeline = ({ tl }) => {
  const KEYS = ["created", "sent", "accept", "start", "submit", "request_revision", "approve", "complete"];
  const LABELS = {
    created: "Created", sent: "Task Sent", accept: "Accepted", start: "Work Started",
    submit: "File Submitted", request_revision: "Revision", approve: "Approved", complete: "Completed",
  };
  const done = new Set((tl || []).map(t => t.key));
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="dw-timeline">
      {KEYS.map((k, i) => {
        const on = done.has(k);
        return (
          <React.Fragment key={k}>
            <div className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-full border transition-colors`}
                 style={{
                   background: on ? "rgba(16,185,129,0.10)" : "var(--surface-2)",
                   borderColor: on ? "rgba(16,185,129,0.35)" : "var(--border)",
                   color: on ? "#10B981" : "var(--text-dim)",
                 }}>
              <span className="w-1 h-1 rounded-full" style={{ background: on ? "#10B981" : "var(--text-dim)" }}/>
              {LABELS[k]}
            </div>
            {i < KEYS.length - 1 && <div className="w-2 h-px" style={{ background: "var(--border)" }}/>}
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default function DirectWorkThread({ dwId, backTo }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dw, setDw] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const [{ data: d1 }, { data: d2 }] = await Promise.all([
        api.get(`/direct-work/${dwId}`),
        api.get(`/direct-work/${dwId}/messages`),
      ]);
      setDw(d1); setMsgs(d2);
      await api.post(`/direct-work/${dwId}/read`).catch(() => {});
    } catch (e) {
      toast.error("Failed to load conversation");
    }
  };

  const poll = async () => {
    if (!msgs.length) return load();
    const after = msgs[msgs.length - 1].created_at;
    try {
      const { data } = await api.get(`/direct-work/${dwId}/messages`, { params: { after } });
      if (data.length) {
        setMsgs(m => [...m, ...data]);
        await api.post(`/direct-work/${dwId}/read`).catch(() => {});
        // Refresh DW status too
        const { data: d1 } = await api.get(`/direct-work/${dwId}`);
        setDw(d1);
      }
    } catch (_) {}
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dwId]);
  useEffect(() => {
    const iv = setInterval(poll, 3500);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [dwId, msgs]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [msgs.length]);

  const onFilePick = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/uploads?purpose=direct_work", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push({ file_id: data.file_id, original_filename: data.original_filename, size: data.size });
      }
      setPendingFiles(p => [...p, ...uploaded]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const send = async () => {
    if (!text.trim() && !pendingFiles.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/direct-work/${dwId}/messages`, {
        text: text.trim(),
        attachment_file_ids: pendingFiles.map(f => f.file_id),
      });
      setMsgs(m => [...m, data]);
      setText(""); setPendingFiles([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const transition = async (action) => {
    setBusy(true);
    try {
      await api.post(`/direct-work/${dwId}/status`, { action });
      toast.success(`Marked as ${action.replace("_", " ")}`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  const grouped = useMemo(() => {
    const out = []; let last = "";
    for (const m of msgs) {
      const d = fmtDate(m.created_at);
      if (d !== last) { out.push({ separator: d }); last = d; }
      out.push(m);
    }
    return out;
  }, [msgs]);

  if (!dw) {
    return <div className="text-sm py-10 text-center" style={{ color: "var(--text-dim)" }}>Loading…</div>;
  }

  const studentActions = user?.role === "student" ? [
    dw.status === "sent" && { key: "accept",  label: "Accept",     icon: CheckCircle2, primary: true },
    dw.status === "sent" && { key: "reject",  label: "Reject",     icon: XCircle },
    dw.status === "accepted" && { key: "start", label: "Start work", icon: PlayCircle, primary: true },
    (dw.status === "in_progress" || dw.status === "revision") && { key: "submit", label: "Submit", icon: Upload, primary: true },
  ].filter(Boolean) : [];

  const adminActions = isAdmin ? [
    dw.status === "submitted" && { key: "approve", label: "Approve", icon: CheckCircle2, primary: true },
    dw.status === "submitted" && { key: "request_revision", label: "Request revision", icon: RotateCw },
    (dw.status === "approved" || dw.status === "submitted") && { key: "complete", label: "Mark completed", icon: CheckCircle2 },
    dw.status !== "archived" && { key: "archive", label: "Archive", icon: Archive },
  ].filter(Boolean) : [];

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] md:h-[calc(100vh-10rem)] rounded-xl border overflow-hidden"
         data-testid="dw-thread"
         style={{ borderColor: "var(--border)", background: "var(--surface)" }}>
      {/* Header */}
      <div className="flex items-center gap-3 px-3 md:px-5 py-3 border-b"
           style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
        <button
          data-testid="dw-back-btn"
          onClick={() => navigate(backTo)}
          className="p-1 rounded-md hover:bg-white/5 lg:hidden"
          style={{ color: "var(--text-muted)" }}
        >
          <ArrowLeft size={18}/>
        </button>
        <div className="w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0"
             style={{ background: "linear-gradient(135deg,#1e3a8a 0%,#3B82F6 100%)" }}>
          <span className="text-white text-sm font-display font-semibold">A4</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--brand)" }}>
            {isAdmin ? "Direct work · Student" : "Assisto4U Admin · Direct Work"}
          </div>
          <div className="text-sm font-medium truncate" style={{ color: "var(--text)" }}>
            {isAdmin ? dw.student_name : "Assisto4U Admin"} · {dw.direct_work_id}
          </div>
        </div>
        <StatusPill status={dw.status}/>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-3 md:px-5 py-4">
        {/* Task card at top */}
        <div className="mb-4">
          <TaskCard dw={dw}/>
        </div>
        {/* Timeline */}
        <div className="mb-4">
          <Timeline tl={dw.timeline}/>
        </div>

        {/* Messages */}
        {grouped.map((m, i) => m.separator ? (
          <div key={`sep-${i}`} className="my-3 flex items-center gap-2">
            <div className="flex-1 h-px" style={{ background: "var(--border)" }}/>
            <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-dim)" }}>{m.separator}</span>
            <div className="flex-1 h-px" style={{ background: "var(--border)" }}/>
          </div>
        ) : (
          <Bubble key={m.message_id} m={m} mine={m.sender_id === user?.user_id}/>
        ))}
        <div ref={bottomRef}/>
      </div>

      {/* Action bar */}
      {(studentActions.length + adminActions.length) > 0 && (
        <div className="px-3 md:px-5 py-2 border-t flex flex-wrap gap-2"
             style={{ borderColor: "var(--border)", background: "var(--surface-2)" }}>
          {[...studentActions, ...adminActions].map(a => (
            <button
              key={a.key}
              data-testid={`dw-action-${a.key}`}
              disabled={busy}
              onClick={() => transition(a.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs ${a.primary ? "btn-primary" : "btn-ghost"}`}
            >
              <a.icon size={12}/> {a.label}
            </button>
          ))}
        </div>
      )}

      {/* Composer */}
      {dw.status !== "archived" && (
        <div className="px-3 md:px-5 py-3 border-t flex items-end gap-2"
             style={{ borderColor: "var(--border)", background: "var(--surface)", paddingBottom: "max(env(safe-area-inset-bottom), 12px)" }}>
          <button
            data-testid="dw-attach-btn"
            onClick={() => fileInputRef.current?.click()}
            className="p-2 rounded-md hover:bg-white/5"
            style={{ color: "var(--text-muted)" }}
          >
            <Paperclip size={16}/>
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            data-testid="dw-file-input"
            onChange={onFilePick}
            accept=".pdf,image/*"
          />
          <div className="flex-1 min-w-0">
            {pendingFiles.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1">
                {pendingFiles.map(f => (
                  <span key={f.file_id} className="text-[11px] px-2 py-0.5 rounded-full border inline-flex items-center gap-1"
                        style={{ borderColor: "var(--border-strong)", color: "var(--text-muted)" }}>
                    <Paperclip size={10}/> {f.original_filename}
                    <button onClick={() => setPendingFiles(p => p.filter(x => x.file_id !== f.file_id))} className="ml-1 hover:text-rose-500">×</button>
                  </span>
                ))}
              </div>
            )}
            <textarea
              data-testid="dw-msg-input"
              value={text}
              onChange={e => setText(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={1}
              placeholder="Type a message…"
              className="w-full resize-none bg-transparent outline-none text-sm py-1.5 max-h-32"
              style={{ color: "var(--text)" }}
            />
          </div>
          <button
            data-testid="dw-send-btn"
            onClick={send}
            disabled={busy || (!text.trim() && !pendingFiles.length)}
            className="btn-primary p-2.5 rounded-md disabled:opacity-50"
          >
            <Send size={14}/>
          </button>
        </div>
      )}
    </div>
  );
}
